/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React components
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";
import routesAdmin from "routesAdmin";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";
import { registerPushSubscription } from "utils/push";
import { BRAND_LOGOS, BRAND_NAME } from "config/brand";

const sideNavBrandLogo = BRAND_LOGOS.light;

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const safeParse = (raw) => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  // Auth-Status + Profil aus localStorage lesen
  const isAuthenticated = !!localStorage.getItem("solvit_auth_token");
  const profileRaw = localStorage.getItem("solvit_profile");
  const profile = profileRaw ? safeParse(profileRaw) : null;
  const isAdmin = (profile?.role || "").toLowerCase() === "admin";
  const displayBrandName =
    profile?.companyName ||
    profile?.contactName ||
    (isAdmin ? `${BRAND_NAME} Admin` : `${BRAND_NAME} Portal`);

  useEffect(() => {
    if (!isAuthenticated) return;
    void registerPushSubscription();
  }, [isAuthenticated]);

  // Routen je nach Rolle (Admin bekommt eigene Sidebar + Unterseiten)
  const activeRoutes = isAdmin ? routesAdmin : routes;

  // Alle Routen aus routes.js verarbeiten und private Routen schützen
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (!route.route) return null;

      if (route.private) {
        // Admin-only guard: route exists but is inaccessible for non-admin users
        if (route.adminOnly && !isAdmin) {
          return (
            <Route
              exact
              path={route.route}
              key={route.key}
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Navigate to="/authentication/sign-in" replace />
                )
              }
            />
          );
        }

        return (
          <Route
            exact
            path={route.route}
            key={route.key}
            element={
              isAuthenticated ? (
                route.component
              ) : (
                <Navigate to="/authentication/sign-in" replace />
              )
            }
          />
        );
      }

      // Public route (e.g., login). If adminOnly is set, hide/redirect for non-admins.
      if (route.adminOnly && !isAdmin) {
        return (
          <Route
            exact
            path={route.route}
            key={route.key}
            element={<Navigate to="/dashboard" replace />}
          />
        );
      }

      return (
        <Route
          exact
          path={route.route}
          key={route.key}
          element={route.component}
        />
      );
    });

  const sidenavRoutes = activeRoutes;

  const renderRoutes = (
    <Routes>
      {/* Root: je nach Login-Status */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to={isAdmin ? "/admin/dashboard" : "/dashboard"} replace />
          ) : (
            <Navigate to="/authentication/sign-in" replace />
          )
        }
      />

      {getRoutes(activeRoutes)}

      {/* Fallback: alles andere passend umbiegen */}
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Navigate to={isAdmin ? "/admin/dashboard" : "/dashboard"} replace />
          ) : (
            <Navigate to="/authentication/sign-in" replace />
          )
        }
      />
    </Routes>
  );

  // RTL-Variante
  if (direction === "rtl") {
    return (
      <CacheProvider value={rtlCache}>
        <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
          <CssBaseline />
          {layout === "dashboard" && isAuthenticated && (
            <Sidenav
              color={sidenavColor}
              brand={sideNavBrandLogo}
              brandName={displayBrandName}
              routes={sidenavRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}
          {renderRoutes}
        </ThemeProvider>
      </CacheProvider>
    );
  }

  // LTR-Variante
  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && isAuthenticated && (
        <Sidenav
          color={sidenavColor}
          brand={sideNavBrandLogo}
          brandName={displayBrandName}
          routes={sidenavRoutes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      {renderRoutes}
    </ThemeProvider>
  );
}
